import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL, getAccessToken } from '../api';
import { analytics, AnalyticsConsent, CONSENT_KEY, readAnalyticsConsent } from '../analytics';
import { getActivePromotion, recordPromotionEvent, PromotionPlacement, PublicPromotion } from '../api/promotions';
import './PromotionSlot.css';

type Props = { placement: PromotionPlacement; compact?: boolean; className?: string };
function assetUrl(value: string): string | null {
  try {
    const url = new URL(value, API_BASE_URL);
    return url.origin === new URL(API_BASE_URL).origin && /^\/promotions\/assets\/[a-f0-9]{24}$/.test(url.pathname)
      && !url.search && !url.hash && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}
function targetUrl(value: string): string | null {
  try {
    const url = new URL(value);
    const unsafe = Array.from(value).some(char => char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127 || char === '\\');
    return url.protocol === 'https:' && !url.username && !url.password && !unsafe ? url.href : null;
  } catch { return null; }
}

/** No campaigns are requested or embedded while the SEO snapshot is generated. */
export default function PromotionSlot(props: Props) {
  const location = useLocation();
  const { language } = useLanguage();
  const { user, loading } = useAuth();
  if (window.__GL_PRERENDER__ || loading || (getAccessToken() && !user)) return null;
  return <Placement key={`${location.pathname}:${language}:${user?.email || 'guest'}:${user?.role || ''}`}
    {...props} language={language === 'kk' ? 'kk' : 'ru'} path={location.pathname} user={user} />;
}

function Placement({ placement, compact = false, className = '', language, path, user }: Props & {
  language: 'ru' | 'kk'; path: string; user: { role: string; email: string } | null;
}) {
  const [item, setItem] = useState<PublicPromotion | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [mobile, setMobile] = useState(() => compact || window.matchMedia?.('(max-width: 767px)').matches === true);
  const [consent, setConsent] = useState<AnalyticsConsent>(readAnalyticsConsent);
  const consentRef = useRef(consent); consentRef.current = consent;
  const current = useRef(item); current.current = item;
  const sent = useRef({ impression: false, click: false });
  const anchor = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (compact || !window.matchMedia) return;
    const media = window.matchMedia('(max-width: 767px)');
    const change = () => setMobile(media.matches);
    media.addEventListener('change', change); change();
    return () => media.removeEventListener('change', change);
  }, [compact]);
  useEffect(() => {
    const change = (event: Event) => {
      const value = (event as CustomEvent).detail;
      if (value === 'granted' || value === 'denied') { consentRef.current = value; setConsent(value); }
    };
    const storage = (event: StorageEvent) => {
      if (event.key === CONSENT_KEY || event.key === null) { const value = readAnalyticsConsent(); consentRef.current = value; setConsent(value); }
    };
    window.addEventListener('gl:analytics-consent', change); window.addEventListener('storage', storage);
    return () => { window.removeEventListener('gl:analytics-consent', change); window.removeEventListener('storage', storage); };
  }, []);
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    getActivePromotion(placement, language, undefined, controller.signal).then(({ item: candidate }) => {
      if (alive && candidate && candidate.placement === placement && Date.parse(candidate.ends_at) > Date.now()
        && assetUrl(candidate.desktop_url) && assetUrl(candidate.mobile_url) && targetUrl(candidate.target_url)) setItem(candidate);
    }).catch(() => { /* Campaign outages must not interrupt the page. */ });
    return () => { alive = false; controller.abort(); };
  }, [placement, language]);
  useEffect(() => {
    if (!item) return;
    let alive = true, pending = false;
    const controller = new AbortController();
    const id = item.id, revision = item.revision;
    let expiry: number;
    const checkExpiry = () => {
      if (!alive) return;
      const remaining = Date.parse(item.ends_at) - Date.now();
      if (remaining <= 0) setItem(null);
      else expiry = window.setTimeout(checkExpiry, Math.min(2147483647, remaining));
    };
    checkExpiry();
    const revalidate = async () => {
      if (document.visibilityState === 'hidden' || pending) return;
      pending = true;
      try {
        const { item: fresh } = await getActivePromotion(placement, language, id, controller.signal);
        if (!alive) return;
        // Never rotate or change a creative in the middle of reading a page.
        if (!fresh || fresh.id !== id || fresh.revision !== revision || Date.parse(fresh.ends_at) <= Date.now()) setItem(null);
        // The original ticket identifies this view. A new ticket would break
        // cross-request deduplication; long-open expired tickets stay unmeasured.
      } catch { if (alive) setItem(null); }
      finally { pending = false; }
    };
    const interval = window.setInterval(revalidate, 45000);
    window.addEventListener('focus', revalidate); document.addEventListener('visibilitychange', revalidate);
    return () => { alive = false; controller.abort(); clearTimeout(expiry); clearInterval(interval);
      window.removeEventListener('focus', revalidate); document.removeEventListener('visibilitychange', revalidate); };
    // Ticket refreshes must not restart the schedule timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item?.revision, item?.ends_at, placement, language]);

  const canMeasure = () => {
    if (consentRef.current !== 'granted' || (user && user.role !== 'user')) return false;
    const token = getAccessToken();
    if (!!token !== !!user) return false;
    if (token && user) {
      try { if (JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub !== user.email) return false; }
      catch { return false; }
    }
    return true;
  };
  const measure = (kind: 'impression' | 'click') => {
    const ad = current.current;
    if (!ad?.metrics_token || sent.current[kind] || !canMeasure() || Date.parse(ad.ends_at) <= Date.now()) return;
    sent.current[kind] = true;
    void recordPromotionEvent(ad.metrics_token, kind).catch(() => {});
    analytics.promotion(kind, ad.id, ad.revision, placement, path);
  };
  useEffect(() => {
    if (!loaded || !item || consent !== 'granted' || (user && user.role !== 'user') || !window.IntersectionObserver) return;
    let visible = false, timer: ReturnType<typeof setTimeout> | undefined;
    const cancel = () => { clearTimeout(timer); timer = undefined; };
    const check = () => {
      cancel();
      if (visible && document.visibilityState !== 'hidden' && !sent.current.impression)
        timer = setTimeout(() => { if (visible && document.visibilityState !== 'hidden') measure('impression'); }, 1000);
    };
    const observer = new IntersectionObserver(entries => { visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5); check(); }, { threshold: [0, 0.5, 1] });
    if (anchor.current) observer.observe(anchor.current);
    document.addEventListener('visibilitychange', check);
    return () => { cancel(); observer.disconnect(); document.removeEventListener('visibilitychange', check); };
    // Refs preserve deduplication across ticket refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, item?.id, item?.revision, consent, user?.role]);

  if (!item) return null;
  const desktop = assetUrl(item.desktop_url), phone = assetUrl(item.mobile_url), href = targetUrl(item.target_url);
  if (!desktop || !phone || !href) return null;
  return <aside className={`promotion-slot ${compact ? 'promotion-slot--compact' : ''} ${className}`}
    aria-label={language === 'kk' ? 'Жарнама' : 'Реклама'} data-promotion-placement={placement}>
    <p className="promotion-slot__label">{language === 'kk' ? 'Жарнама' : 'Реклама'} · {item.advertiser}</p>
    <a ref={anchor} className="promotion-slot__link" href={href} target="_blank" rel="sponsored noopener noreferrer"
      referrerPolicy="no-referrer" onClick={() => measure('click')}>
      <picture>
        {!compact && <source media="(max-width: 767px)" srcSet={phone} />}
        <img src={compact ? phone : desktop} width={compact ? 720 : 2400} height={compact ? 240 : 300}
          alt={mobile ? item.alt_mobile : item.alt_desktop} decoding="async" loading="lazy"
          onLoad={() => setLoaded(true)} onError={() => setItem(null)} />
      </picture>
    </a>
  </aside>;
}
